import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Portal,
} from '@headlessui/react'
import { Bars2Icon } from '@heroicons/react/24/solid'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { motion } from 'framer-motion'
// import { Link } from './link'
import { Link, useLocation, useNavigate } from "react-router-dom"
import { LogoTextOnly } from './logo'
import { PlusGrid, PlusGridItem, PlusGridRow } from './plusGrid'
import Button from './Button'

import clsx from 'clsx'
import React, { useEffect, useState, useRef } from 'react'



const BodyScrollLock = ({ active }: { active: boolean }) => {
  useEffect(() => {
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    if (active) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    }
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [active]);
  return null;
};

const DesktopNav = ({ set_demo_modal_is_open, journey }) => {
  return (
    <nav className="relative hidden sm:flex items-center gap-4">
      <Button to="#demo" variant="secondary">Book a demo</Button>
            <Link
        to="https://app.vitalstrata.com/login"
        className="text-white text-base font-light rounded-none px-4 py-2 transition bg-transparent hover:bg-white/10 focus:bg-white/10 active:bg-white/20 focus:outline-none border-none cursor-pointer no-underline"
      >
        Login
            </Link>
    </nav>
  )
}

const MobileNavButton = ({ scrolled }) => {
  return (
    <DisclosureButton
      className={clsx(
        "flex size-12 items-center justify-center self-center rounded-lg lg:hidden focus:outline-none",
        "text-white hover:bg-white/5"
      )}
      aria-label="Open main menu"
    >
      <Bars2Icon className="size-6" />
    </DisclosureButton>
  )
}

const MobileNav = ({ set_demo_modal_is_open, close, journey, scrolled }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollToId = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  const handleAnchor = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/#' + id);
      setTimeout(() => scrollToId(id), 0);
    } else {
      scrollToId(id);
    }
    close();
  };
  const rafMenuItems: Array<{ id: string; label: string }> = [
    { id: 'raf-precision-ai', label: 'Highlights' },
    { id: 'pilot', label: 'Pilot' },
    { id: 'value-propositions', label: 'Value propositions' },
    { id: 'performance-security-safety', label: 'Performance · Security · Safety' },
  ];
  return (
    <Portal>
      <DisclosurePanel className="lg:hidden fixed inset-x-0 bottom-0 top-20 z-[3500] overflow-y-auto">
        <div className="flex flex-col gap-3 py-4">
          <Link
            to="/demo#top"
            className={clsx(
              "text-lg font-light rounded-none px-4 py-3 mx-4 transition bg-transparent focus:outline-none border-none cursor-pointer text-left no-underline",
              "text-white hover:bg-white/10 focus:bg-white/10 active:bg-white/20"
            )}
            onClick={close}
          >
            Book a demo
          </Link>
          <Link
            to="https://app.vitalstrata.com/login"
            className={clsx(
              "text-lg font-light rounded-none px-4 py-3 mx-4 transition bg-transparent focus:outline-none border-none cursor-pointer text-left no-underline",
              "text-white hover:bg-white/10 focus:bg-white/10 active:bg-white/20"
            )}
            onClick={close}
          >
            Login
          </Link>
          <div className="mt-2 mx-4 h-px bg-white/10" />
          <div className="mx-4 mt-1 text-sm font-light text-white/60">
            RAF Precision AI
          </div>
          {rafMenuItems.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={handleAnchor(item.id)}
              className={clsx(
                "text-lg font-light rounded-none px-4 py-3 mx-4 transition bg-transparent focus:outline-none border-none cursor-pointer text-left no-underline",
                "text-white hover:bg-white/10 focus:bg-white/10 active:bg-white/20"
              )}
            >
              {item.label}
            </a>
          ))}
          <div className="mt-2 mx-4 h-px bg-white/10" />
          <Link
            to="https://blog.vitalstrata.com"
            target="_blank"
            className={clsx(
              "text-lg font-light rounded-none px-4 py-3 mx-4 transition bg-transparent focus:outline-none border-none cursor-pointer text-left no-underline",
              "text-white hover:bg-white/10 focus:bg-white/10 active:bg-white/20"
            )}
            onClick={close}
          >
            Blog
          </Link>
          <Link
            to="https://careers.vitalstrata.com"
            target="_blank"
            className={clsx(
              "text-lg font-light rounded-none px-4 py-3 mx-4 transition bg-transparent focus:outline-none border-none cursor-pointer text-left no-underline",
              "text-white hover:bg-white/10 focus:bg-white/10 active:bg-white/20"
            )}
            onClick={close}
          >
            Careers
          </Link>
        </div>
        <div className="absolute left-1/2 w-screen -translate-x-1/2">
          <div className="absolute inset-x-0 top-0 border-t border-white/10" />
          <div className="absolute inset-x-0 top-2 border-t border-white/10" />
        </div>
      </DisclosurePanel>
    </Portal>
  )
}

const Navbar = ({ 
  banner, 
  set_demo_modal_is_open, 
  journey 
} : { 
  banner?: React.ReactNode, 
  set_demo_modal_is_open: any, 
  journey: any 
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [hoverActive, setHoverActive] = useState(false);
  const [menuClickedOpen, setMenuClickedOpen] = useState(false);
  const [suppressHover, setSuppressHover] = useState(false);
  const hoverTimerRef = useRef<number | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });

  const clearHoverTimer = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };
  const openViaHover = () => {
    if (suppressHover) return;
    clearHoverTimer();
    setHoverActive(true);
  };
  const scheduleCloseHover = () => {
    clearHoverTimer();
    hoverTimerRef.current = window.setTimeout(() => {
      setHoverActive(false);
      setSuppressHover(false);
      setMenuClickedOpen(false);
    }, 140);
  };

  const updateMenuPosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Align left edges similar to previous -left-4 and a small gap below
    setMenuPos({ left: Math.max(8, rect.left - 16), top: rect.bottom + 8 });
  };

  useEffect(() => {
    if (menuClickedOpen || (!suppressHover && hoverActive)) {
      updateMenuPosition();
      const onWin = () => updateMenuPosition();
      window.addEventListener('scroll', onWin, { passive: true });
      window.addEventListener('resize', onWin, { passive: true });
      return () => {
        window.removeEventListener('scroll', onWin);
        window.removeEventListener('resize', onWin);
      };
    }
  }, [menuClickedOpen, hoverActive, suppressHover]);

  const navigate = useNavigate();
  const location = useLocation();

  const scrollToId = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleAnchor = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/#' + id);
      // allow the new page to paint first
      setTimeout(() => scrollToId(id), 0);
    } else {
      scrollToId(id);
    }
    // Close dropdown after navigation
    setHoverActive(false);
    setSuppressHover(false);
  };

  const rafMenuItems: Array<{ id: string; label: string }> = [
    { id: 'raf-precision-ai', label: 'Highlights' },
    { id: 'pilot', label: 'Pilot' },
    { id: 'value-propositions', label: 'Value propositions' },
    { id: 'performance-security-safety', label: 'Performance · Security · Safety' },
  ];

  useEffect(() => {
    const onScroll = () => {
      // Add background once the user scrolls a bit past the top
      setScrolled(window.scrollY > 200);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <Disclosure
      as="header"
      className={clsx(
        "fixed top-0 left-0 right-0 z-[4000] duration-300",
        scrolled
          ? "bg-[#0C101D]/50 backdrop-blur-md"
          : "bg-transparent"
      )}
    >
      {({ open, close }) => (
        <>
          <BodyScrollLock active={open} />
          {open && (
            <Portal>
              <div
                className={clsx(
                  "lg:hidden fixed inset-0 z-[3000] pointer-events-auto bg-[#0C101D]/70 supports-[backdrop-filter]:backdrop-blur-md backdrop-blur-md"
                )}
                onClick={() => close()}
              />
            </Portal>
          )}
          <div className={clsx(
            "relative z-20 flex justify-between items-center w-full",
            "px-10 py-6 sm:px-8 sm:py-4"
          )}>
            <div className="relative flex gap-16">
              <Link to="/" title="Home">
                <LogoTextOnly className="h-8" fill="#fff" />
              </Link>
              {/* Left-side nav next to logo */}
              <div className="hidden lg:flex items-center gap-12">
                {/* RAF Precision AI dropdown */}
                <Menu as="div" className="relative" >
                  {({ open }) => {
                    const show = menuClickedOpen || (!suppressHover && hoverActive);
                    return (
                    <>
                      <div
                        ref={triggerRef}
                        onMouseEnter={openViaHover}
                        onMouseLeave={scheduleCloseHover}
                        className="relative"
                      >
                        <MenuButton
                          className="flex items-center gap-1 text-white text-base font-light hover:text-white/90 focus:outline-none"
                          onClick={(e) => {
                            e.preventDefault();
                            setMenuClickedOpen((prev) => {
                              // If open due to hover (not click), clicking should close and suppress hover until leave
                              if (hoverActive && !prev) {
                                setSuppressHover(true);
                                setHoverActive(false);
                                return false;
                              }
                              const next = !prev;
                              if (next) setSuppressHover(false);
                              return next;
                            });
                          }}
                        >
                          RAF Precision AI
                          <ChevronDownIcon
                          className={clsx(
                            "h-4 w-4 transition-transform",
                            show ? "rotate-0" : "-rotate-90"
                          )}
                          aria-hidden
                        />
                        </MenuButton>
                        <Portal>
                          <MenuItems
                            static
                            className={clsx(
                              "fixed min-w-64 rounded-none p-1",
                              "bg-[#0C101D]/70 supports-[backdrop-filter]:backdrop-blur-md backdrop-blur-md border border-white/10 shadow-lg overflow-hidden",
                              "text-left pointer-events-auto z-[5000]",
                              show ? "block" : "hidden"
                            )}
                            style={{ left: menuPos.left, top: menuPos.top }}
                            onMouseEnter={openViaHover}
                            onMouseLeave={scheduleCloseHover}
                          >
                          {rafMenuItems.map((item) => (
                            <MenuItem key={item.id}>
                              {({ active }) => (
                                <a
                                  href={`#${item.id}`}
                                  onClick={(e) => {
                                    handleAnchor(item.id)(e);
                                    setMenuClickedOpen(false);
                                    setHoverActive(false);
                                    setSuppressHover(false);
                                  }}
                                  className={clsx(
                                    "block w-full text-left px-3 py-2 text-base rounded-none font-light",
                                    active
                                      ? "bg-white/15 text-white"
                                      : "text-white/90 hover:bg-white/10 hover:text-white"
                                  )}
                                >
                                  {item.label}
                                </a>
                              )}
                            </MenuItem>
                          ))}
                          </MenuItems>
                        </Portal>
                      </div>
                    </>
                    )}
                  }
                </Menu>
                <Link
                  to="https://blog.vitalstrata.com"
                  target="_blank"
                  className="text-white text-base hover:text-white/90 no-underline font-light"
                >
                  Blog
                </Link>
                <Link
                  to="https://careers.vitalstrata.com"
                  target="_blank"
                  className="text-white text-base hover:text-white/90 no-underline font-light"
                >
                  Careers
                </Link>
              </div>
              {banner && (
                <div className="relative hidden items-center py-3 xl:flex">
                  {banner}
                </div>
              )}
            </div>
            <nav className="relative hidden lg:flex items-center gap-4">
              <Button to="/demo#top" variant="secondary" className="rounded-none font-normal" >
                Book a demo
              </Button>
              <Link
                to="https://app.vitalstrata.com/login"
                className={clsx(
                  "text-base font-light rounded-none  px-4 py-2 transition bg-transparent hover:bg-white/10 focus:bg-white/10 active:bg-white/20 focus:outline-none border-none cursor-pointer no-underline",
                  "text-white"
                )}
              >
                Login
              </Link>
            </nav>
            <MobileNavButton scrolled={false} />
          </div>
          <MobileNav set_demo_modal_is_open={set_demo_modal_is_open} close={close} journey={journey} scrolled={false} />
        </>
      )}
    </Disclosure>
  );
}

export default Navbar;